import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { getHomeRouteByRole, normalizeRole } from "../utils/authRoles";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { setLoading } from "../redux/loaderSlice";
import { validateLoginForm } from "../utils/validations";
import LoginImage from '../../src/assets/Login.png'
import { Eye, EyeOff } from 'lucide-react';

const PasswordWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    color: #64748b;
  }
`;

const PageWrapper = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: #f8fafc;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const LeftSide = styled.div`
  flex: 7;
  /* 🖼️ IMAGE PROVISION: Replace the URL below with your actual image path. */
  background: 
    
    url(${LoginImage}) center/cover no-repeat;
  background-color: #0f172a; /* Fallback color */
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  color: white;
  overflow: hidden;
  padding-bottom: 80px;

  /* Gold accent glow */
  &::before {
    content: '';
    position: absolute;
    top: -20%;
    left: -10%;
    width: 60%;
    height: 60%;
    background: radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -10%;
    right: -10%;
    width: 50%;
    height: 50%;
    background: radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%);
    border-radius: 50%;
  }

  @media (max-width: 900px) {
    flex: none;
    height: 40vh;
    min-height: 280px;
    padding: 0;
    padding-bottom: 35px; /* Added spacing to prevent overlap with the form card */
    justify-content: flex-end;
    align-items: center;
    text-align: center;
  }
`;

const GlassContent = styled.div`
  position: relative;
  z-index: 10;
  padding: 0px 40px 0px 80px;
  width: 100%;
  text-align: left;
  /* Soft dark fade starting from the bottom to ensure crisp text readability against bright background images */
  background: linear-gradient(to top, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.5) 60%, transparent 100%);

  @media (max-width: 900px) {
    padding: 20px 16px 32px 16px;
    text-align: center;
    background: linear-gradient(to top, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.3) 100%);
  }
`;

const BrandTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 0px;
  color: #ffffff;
  letter-spacing: -0.02em;
  margin-top: 0;
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.9), 0 2px 6px rgba(0, 0, 0, 0.9);
  -webkit-text-stroke: 1px rgba(0, 0, 0, 0.2);
  
  span {
    color: #D4AF37;
    text-shadow: 0 4px 16px rgba(0, 0, 0, 0.8);
  }

  @media (max-width: 900px) {
    font-size: 1.75rem;
    margin-bottom: 6px;
  }
`;

const BrandSubtitle = styled.p`
  font-size: 1.25rem;
  color: #f1f5f9;
  font-weight: 400;
  line-height: 1.6;
  margin: 0;
  max-width: 500px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.9), 0 1px 3px rgba(0, 0, 0, 0.8);

  @media (max-width: 900px) {
    font-size: 0.95rem;
  }
`;

const RightSide = styled.div`
  // flex: 3;
  min-width: 440px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  // padding: 40px;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.05);
  z-index: 20;

  @media (max-width: 900px) {
    margin-top: 20px;
    background-color: transparent;
    // flex: 1;
    min-width: 100%;
  }
`;

const LoginFormContainer = styled.form`
  width: 100%;
  max-width: 380px;
`;

const FormHeader = styled.div`
  margin-bottom: 32px;
  text-align: left;

  h2 {
    font-size: 1.875rem;
    color: #0f172a;
    margin-bottom: 8px;
    font-weight: 700;
    margin-top: 0;
  }

  p {
    color: #64748b;
    font-size: 1rem;
    margin: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ hasError }) => (hasError ? "#ef4444" : "#e2e8f0")};
  border-radius: 8px;
  outline: none;
  font-size: 1rem;
  transition: all 0.2s ease-in-out;
  background: #f8fafc;
  color: #1e293b;

  &:focus {
    background: #ffffff;
    border-color: ${({ hasError }) => (hasError ? "#ef4444" : "#D4AF37")};
    box-shadow: 0 0 0 3px ${({ hasError }) => (hasError ? "rgba(239,68,68,0.1)" : "rgba(212,175,55,0.15)")};
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const ErrorText = styled.span`
  color: #ef4444;
  font-size: 0.8rem;
  display: block;
  margin-top: 8px;
  font-weight: 500;
`;

// const GeneralError = styled.div`
//   background: #fef2f2;
//   border: 1px solid #fecaca;
//   color: #dc2626;
//   padding: 12px;
//   border-radius: 8px;
//   font-size: 0.875rem;
//   font-weight: 500;
//   margin-bottom: 20px;
//   text-align: center;
// `;

// const ActionRow = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   margin-bottom: 28px;
//   font-size: 0.875rem;
// `;

// const CheckboxLabel = styled.label`
//   display: flex;
//   align-items: center;
//   color: #475569;
//   cursor: pointer;
//   font-weight: 500;

//   input {
//     margin-right: 8px;
//     accent-color: #D4AF37;
//     width: 16px;
//     height: 16px;
//     cursor: pointer;
//   }
// `;

// const ForgotLink = styled.a`
//   color: #D4AF37;
//   font-weight: 600;
//   text-decoration: none;
//   cursor: pointer;
//   transition: color 0.15s ease-in-out;

//   &:hover {
//     color: #b49010;
//   }
// `;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #D4AF37 0%, #b49010 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(212, 175, 55, 0.25);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(212, 175, 55, 0.35);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(212, 175, 55, 0.25);
  }

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    color: #f1f5f9;
  }
`;

const FormFooter = styled.p`
  margin-top: 32px;
  text-align: center;
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  svg {
    width: 14px;
    height: 14px;
    color: #D4AF37;
  }
`;

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { authUser, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const loading = useSelector((state) => state.loader.loading);
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const { errors: vErrors } = validateLoginForm({ email, pass });
    const filteredErrors = Object.entries(vErrors).reduce((acc, [key, message]) => {
      if (touchedFields[key]) {
        acc[key] = message;
      }
      return acc;
    }, {});
    setFormErrors(filteredErrors);
  }, [email, pass, touchedFields]);

  const validateField = (key) => {
    setTouchedFields((prev) => ({ ...prev, [key]: true }));
  };

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (authUser?.role) {
      navigate(getHomeRouteByRole(authUser.role), { replace: true });
    }
  }, [authLoading, authUser, navigate]);

  const getLoginError = (firebaseCode) => {
    switch (firebaseCode) {
      case "auth/invalid-credential":
      case "auth/user-not-found":
      case "auth/wrong-password":
        return "Invalid email or password";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/too-many-requests":
        return "Too many failed attempts. Try again later.";
      case "auth/network-request-failed":
        return "Network error. Check your internet connection.";
      case "auth/configuration-not-found":
        return "Firebase Auth is not configured for this project. Verify your Firebase .env.local values and Authentication setup.";
      case "permission-denied":
        return "You do not have permission to access your profile.";
      case "unavailable":
        return "Service is temporarily unavailable. Please try again.";
      default:
        return "Unable to login now. Please try again.";
    }
  };

  const handleLogin = async (event) => {
    event?.preventDefault();
    if (loading || authLoading) return;

    const validation = validateLoginForm({ email, pass });
    if (!validation.isValid) {
      const markAllTouched = Object.keys(validation.errors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouchedFields((prev) => ({ ...prev, ...markAllTouched }));
      toast.error("Please fix form errors.");
      return;
    }

    const normalizedEmail = email.trim();
    dispatch(setLoading(true));
    let signedInUser = null;

    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        pass
      );
      signedInUser = credential.user;
      const userRef = doc(db, "users", credential.user.uid);
      const userSnapshot = await getDoc(userRef);
      const role = normalizeRole(userSnapshot.data()?.role);

      if (!role) {
        await signOut(auth);
        toast.error("Your account has no role assigned. Contact an admin.");
        return;
      }

      toast.success("Login successful.");
      navigate(getHomeRouteByRole(role), { replace: true });
    } catch (err) {
      if (signedInUser && auth.currentUser?.uid === signedInUser.uid) {
        await signOut(auth);
      }
      toast.error(getLoginError(err?.code));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <PageWrapper>
      <LeftSide>
        <GlassContent>
          <BrandTitle>
            MAP'S <span>ASSOCIATES</span>
          </BrandTitle>
          <BrandSubtitle>
            Secure Gold Loan & Financial Management Platform
          </BrandSubtitle>
        </GlassContent>
      </LeftSide>

      <RightSide>
        <LoginFormContainer onSubmit={handleLogin} noValidate>
          <FormHeader>
            <h2>Welcome Back</h2>
            <p>Please enter your details to sign in.</p>
          </FormHeader>

          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              hasError={!!formErrors.email}
              onChange={(e) => {
                setEmail(e.target.value);
                validateField("email");
              }}
              onBlur={() => validateField("email")}
              autoComplete="email"
            />
            {formErrors.email && <ErrorText>{formErrors.email}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <PasswordWrapper>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={pass}
                hasError={!!formErrors.pass}
                onChange={(e) => {
                  setPass(e.target.value);
                  validateField("pass");
                }}
                onBlur={() => validateField("pass")}
                autoComplete="current-password"
                style={{ paddingRight: "48px" }}
              />
              <ToggleButton
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </ToggleButton>
            </PasswordWrapper>
            {formErrors.pass && <ErrorText>{formErrors.pass}</ErrorText>}
          </FormGroup>

          {/* <ActionRow>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember me
            </CheckboxLabel>
            <ForgotLink onClick={() => toast("Contact admin to reset password.", { icon: "ℹ️" })}>
              Forgot Password?
            </ForgotLink>
          </ActionRow> */}

          <SubmitButton
            type="submit"
            disabled={loading || authLoading || Object.keys(formErrors).length > 0}
          >
            {loading ? "Authenticating..." : "Sign In"}
          </SubmitButton>

          <FormFooter>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            Secure Login • Authorized Access Only
          </FormFooter>
        </LoginFormContainer>
      </RightSide>
    </PageWrapper>
  );
}